/**
 * Normalize a value for comparison.
 */
export function normalize(val) {
  return val === null || val === undefined || val === '' || val === '-'
    ? null
    : String(val).trim().toLowerCase();
}

/**
 * Check if a question is required.
 */
export function isRequired(question) {
  return normalize(question['Required?']) === 'yes';
}

/**
 * Check if a question allows multi-select.
 */
export function isMultiSelect(question) {
  return normalize(question['Multi Select']) === 'yes';
}

/**
 * Get the display text for a question.
 */
export function getQuestionText(question) {
  return question.Text && question.Text !== '-' ? question.Text : '';
}

/**
 * Extract info from a DOM element.
 */
export function getElementInfo(element) {
  if (!element) return {};
  return {
    element: element.outerHTML,
    elementClasses: element.className,
    elementId: element.id,
    elementText: element.innerText,
    elementTag: element.tagName,
  };
}

/**
 * Filters questions by matching their "Service Type" field
 * to any of the branch's provided services.
 * If "Service Type" is missing or null, include the question by default.
 *
 * @param {Array} questions - Array of question objects.
 * @param {Array} branchServices - Array of service strings provided by the branch.
 * @returns {Array} Filtered questions.
 */
export function filterQuestionsByServiceType(questions, branchServices) {
  if (!Array.isArray(questions) || !Array.isArray(branchServices)) {
    return questions || [];
  }

  // Normalize branch services to lowercase for comparison
  const normalizedServices = branchServices.map((s) =>
    String(s || '')
      .trim()
      .toLowerCase()
  );

  const filtered = questions.filter((question) => {
    const serviceType = String(question['Service Type'] || '').trim();

    // If no service type specified, include the question
    if (!serviceType || serviceType === '-' || serviceType === '') {
      return true;
    }

    // Handle comma-delimited service types (e.g., "Plumbing, Electrical")
    const serviceTypes = serviceType
      .split(',')
      .map((s) => s.trim().toLowerCase());

    // Check if any of the service types match any branch service
    return serviceTypes.some((st) => normalizedServices.includes(st));
  });
  return filtered;
}

/**
 * Filters questions by matching their "Property Type" field
 * to any of the allowed property types.
 * If "Property Type" is missing, null, empty, or '-', include the question by default.
 *
 * @param {Array} questions - Array of question objects.
 * @param {Array} allowedPropertyTypes - Array of allowed property type strings.
 * @returns {Array} Filtered questions.
 */
export function filterQuestionsByPropertyType(questions, allowedPropertyTypes) {
  if (!Array.isArray(questions) || !Array.isArray(allowedPropertyTypes))
    return [];
  const normalizedAllowedTypes = allowedPropertyTypes.map((t) =>
    String(t).trim().toLowerCase()
  );

  return questions.filter((q) => {
    const propType = q['Property Type'];
    if (!propType || propType === '-' || propType === '') {
      // If missing, null, empty, or '-', include by default
      return true;
    }
    // Split on commas and slashes
    const propTypes = String(propType)
      .split(/[,/]/)
      .map((type) => type.trim().toLowerCase());
    const match = propTypes.some((type) =>
      normalizedAllowedTypes.includes(type)
    );
    return match;
  });
}

// Helper to normalize component format for TextInput variations
export function isTextInputFormat(format) {
  const norm = normalize(format).replace(/\s+/g, '').toLowerCase();
  return norm === 'textinput';
}

/**
 * Find a pricing description and price for a given service key in branch metadata.
 * @param {Object} branchMetaObj - Branch metadata object.
 * @param {String} serviceKey - The key to look for (e.g., 'HVACInstallationAttic Fans').
 * @returns {Object} { description, priceLow, priceHigh, fixedPrice }
 */

export function getPricingInfo(branchMetaObj, serviceKey) {
  if (!branchMetaObj || !serviceKey) {
    return {
      description: '',
      priceLow: undefined,
      priceHigh: undefined,
      fixedPrice: undefined,
    };
  }

  const trimmedKey = serviceKey.trim();
  let raw = branchMetaObj[trimmedKey];

  if (!raw) {
    return {
      description: '',
      priceLow: undefined,
      priceHigh: undefined,
      fixedPrice: undefined,
    };
  }

  // Helper to format a raw numeric string with commas and a $ prefix
  const fmtNum = (numStr) => {
    const n = parseFloat(String(numStr).replace(/,/g, ''));
    if (isNaN(n)) return `$${numStr}`;
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  if (Array.isArray(raw)) {
    const cleanVals = raw
      .map((v) =>
        String(v || '')
          .replace(/[$,]/g, '')
          .trim()
      )
      .filter((v) => v && !isNaN(v));
    if (cleanVals.length >= 2) {
      const formattedLow = formatUSD(parseFloat(cleanVals[0]));
      const formattedHigh = formatUSD(parseFloat(cleanVals[1]));
      return {
        description: `Average price range: ${formattedLow} - ${formattedHigh}`,
        priceLow: cleanVals[0],
        priceHigh: cleanVals[1],
      };
    } else if (cleanVals.length === 1) {
      const formatted = formatUSD(parseFloat(cleanVals[0]));
      return {
        description: `Average price: ${formatted}`,
        fixedPrice: formatted,
      };
    }
  }

  const str = String(raw);
  const rangeMatch = str.match(/\$?([\d,]+)\s*-\s*\$?([\d,]+)/);
  if (rangeMatch) {
    const low = rangeMatch[1].replace(/,/g, '');
    const high = rangeMatch[2].replace(/,/g, '');
    const formattedLow = formatUSD(parseFloat(low));
    const formattedHigh = formatUSD(parseFloat(high));
    return {
      description: `Average price range: ${formattedLow} - ${formattedHigh}`,
      priceLow: low,
      priceHigh: high,
    };
  }

  const fixedMatch = str.match(/\$([\d,]+)/);
  if (fixedMatch) {
    const formatted = formatUSD(parseFloat(fixedMatch[1].replace(/,/g, '')));
    return {
      description: `Average price: ${formatted}`,
      fixedPrice: formatted,
    };
  }
  return { description: str, fixedPrice: undefined };
}

export function formatUSD(amount) {
  if (typeof amount === 'string') {
    amount = parseFloat(amount.replace(/,/g, ''));
  }
  if (isNaN(amount)) return '';
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function cleanBranchName(branchName) {
  // Remove all whitespace and hyphens, then lowercase
  return (branchName || '').replace(/[\s-]+/g, '').toLowerCase();
}

export function cleanedFormType(formType) {
  if (!formType) return '';
  const lower = formType.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Determines if a pill question should be multi-select.
 * Accepts 'yes' (case-insensitive) as multi-select.
 * Defaults to multi-select if field is null, blank, or missing.
 * Returns true for multi-select, false otherwise.
 */
export function isPillMultiSelect(question) {
  const val = question && question['Multi Select'];
  let result;
  if (val === null || val === undefined || String(val).trim() === '') {
    result = true; // default to multi-select
  } else {
    result = String(val).trim().toLowerCase() === 'yes';
  }
  return result;
}

/**
 * Determines if a bullet should be indented under its parent text or a parent with class 'text-component'.
 * Returns true if parentQuestion is a text type or has className 'text-component'.
 * Logs the decision for debugging.
 */
export function shouldIndentBullet(question, parentQuestion) {
  let result = false;
  let reason = '';
  if (!parentQuestion) {
    reason = 'no parentQuestion';
  } else {
    const format = normalize(parentQuestion['Component/Format']);
    const className = parentQuestion.className || '';
    if (format === 'text') {
      result = true;
      reason = 'parent format is text';
    } else if (format === 'pill') {
      result = true;
      reason = 'parent format is pill';
    } else if (className.includes('text-component')) {
      result = true;
      reason = 'parent className includes text-component';
    } else {
      reason = `parent format: ${format}, className: ${className}`;
    }
  }
  return result;
}

/**
 * Finds the parent question of a given child question based on the "Parent ID" field.
 * The parent question is determined by matching the normalized ID of the parent
 * with the "ID" field of questions in the provided array.
 *
 * @param {Array} questions - The array of questions to search within.
 * @param {Object} childQuestion - The child question object containing the "Parent ID" field.
 * @returns {Object|null} - The parent question object if found, null otherwise.
 */
export function findParentQuestion(questions, childQuestion) {
  try {
    if (!Array.isArray(questions) || !childQuestion) return null;
    const parentIdRaw = childQuestion['Parent ID'];
    if (!parentIdRaw || parentIdRaw === '-' || normalize(parentIdRaw) === null)
      return null;
    const parentIdNorm = normalize(parentIdRaw);
    const parent =
      questions.find((q) => normalize(q.ID) === parentIdNorm) || null;
    return parent || null;
  } catch (e) {
    return null;
  }
}

/**
 * Extracts the DNI-swapped phone number from the main page.
 * Looks for phone links outside the modal that have been processed by DNI.
 * Prioritizes extracting from desktop-specific spans to avoid mobile text like "CALL NOW".
 *
 * @returns {string|null} - The DNI phone number if found, null otherwise.
 */
export function extractDNIPhoneFromPage() {
  // Look for phone links on the main page (outside modal) that have been swapped by DNI
  const mainPagePhones = document.querySelectorAll(
    'a[href^="tel:"].phone-link, a[href^="tel:"][data-st-phone], a[href^="tel:"][aria-label*="Call"]'
  );

  for (const phoneLink of mainPagePhones) {
    // Skip if inside modal
    if (phoneLink.closest('.nu-modal, [id*="modal"]')) {
      continue;
    }

    const displayText = phoneLink.textContent?.trim();
    const href = phoneLink.getAttribute('href');
    const ariaLabel = phoneLink.getAttribute('aria-label'); // Try to extract phone from desktop span first
    const desktopSpan = phoneLink.querySelector('[data-show-on="desktop"]');
    const phoneFromSpan = desktopSpan?.textContent?.trim();

    // Extract phone from desktop span (prioritize this to avoid "CALL NOW")
    if (phoneFromSpan && /\(\d{3}\)\s*\d{3}-\d{4}/.test(phoneFromSpan)) {
      return phoneFromSpan;
    }

    // Fallback: Extract phone from full display text using regex
    if (displayText) {
      const phoneMatch = displayText.match(/\(\d{3}\)\s*\d{3}-\d{4}/);
      if (phoneMatch) {
        return phoneMatch[0];
      }
    } // Fallback: extract from aria-label
    if (ariaLabel) {
      const match = ariaLabel.match(/\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/);
      if (match) {
        return match[0];
      }
    }
  }

  return null;
}

export function buildPricingPayload(pricingObj, branchMetaObj, formType) {
  if (!pricingObj) return null;
  const serviceTypeKey = pricingObj['Service Type'] || '';
  const pricingLabel =
    typeof pricingObj.Text === 'string' ? pricingObj.Text : '';
  let pricingInfo = null;
  let pricingDisplay = '';
  let pricingValue = '';
  if (serviceTypeKey) {
    pricingInfo = getPricingInfo(branchMetaObj, serviceTypeKey);
    if (pricingInfo && pricingInfo.fixedPrice) {
      pricingDisplay = `${pricingLabel}: ${pricingInfo.fixedPrice}`;
      pricingValue = pricingInfo.fixedPrice;
    } else if (pricingInfo && pricingInfo.priceLow && pricingInfo.priceHigh) {
      pricingDisplay = `${pricingLabel}: $${pricingInfo.priceLow} - $${pricingInfo.priceHigh}`;
      pricingValue = `$${pricingInfo.priceLow} - $${pricingInfo.priceHigh}`;
    }
  }
  return {
    questionId: pricingObj.ID,
    value: pricingValue,
    questionText: pricingLabel,
    type: 'Pricing',
    selectedLabel: pricingLabel,
    questionObj: pricingObj,
    event: 'PricingDisplay',
    pagePostType: 'frontpage',
    pagePostType2: 'single-page',
    pagePostAuthor: 'nearu',
    schedulerName: formType,
    pricing: pricingDisplay,
  };
}

export function getPricingDisplayInfo(
  questions,
  pillId,
  branchMetaObj,
  getQuestionText,
  normalize,
  getPricingInfo
) {
  let pricingDisplay = '';
  let pricingKeyUsed = '';
  let pricingInfo = null;
  let pricingLabel = '';
  const pricingChild = questions.find(
    (q) =>
      normalize(q['Parent ID']) === normalize(pillId) &&
      normalize(q['Component/Format']) === 'pricing'
  );
  if (pricingChild) {
    const serviceTypeKey = pricingChild['Service Type'] || '';
    pricingLabel = getQuestionText(pricingChild);
    if (serviceTypeKey) {
      pricingInfo = getPricingInfo(branchMetaObj, serviceTypeKey);
      if (pricingInfo && pricingInfo.fixedPrice) {
        pricingDisplay = `${pricingLabel}: ${pricingInfo.fixedPrice}`;
        pricingKeyUsed = serviceTypeKey;
      } else if (pricingInfo && pricingInfo.priceLow && pricingInfo.priceHigh) {
        pricingDisplay = `${pricingLabel}: $${pricingInfo.priceLow} - $${pricingInfo.priceHigh}`;
        pricingKeyUsed = serviceTypeKey;
      }
    }
  }
  return {
    pricingDisplay,
    pricingKeyUsed,
    pricingInfo,
    pricingLabel,
  };
}

/**
 * Filters questions by matching their "Component/Format" field
 * to any of the supported formats.
 * If "Component/Format" is missing, null, empty, or '-', include the question by default.
 *
 * @param {Array} questions - Array of question objects.
 * @param {Array} formats - Array of supported format strings.
 * @returns {Array} Filtered questions.
 */
export function filterQuestionsBySupportedFormats(questions, formats) {
  if (!Array.isArray(questions) || !Array.isArray(formats)) return [];
  const normalizedFormats = formats.map((f) => normalize(f));
  return questions.filter((q) =>
    normalizedFormats.includes(normalize(q['Component/Format']))
  );
}

export function togglePillValue(prevValue, pillId, isMulti) {
  const coerceId = (id) => (id == null ? '' : String(id));
  if (isMulti) {
    const prevArray = Array.isArray(prevValue) ? prevValue : [];
    const wasSelected = prevArray.map(coerceId).includes(coerceId(pillId));
    return wasSelected
      ? prevArray.filter((x) => coerceId(x) !== coerceId(pillId))
      : [...prevArray, coerceId(pillId)];
  } else {
    const wasSelected = coerceId(prevValue) === coerceId(pillId);
    return wasSelected ? undefined : coerceId(pillId);
  }
}

// UUID generator
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Fetch environment setting from branch-metadata-1 (production table)
 * Returns 'production', 'development', or 'staging'
 * Always queries the production branch-metadata-1 table to get the environment setting
 */
export async function fetchEnvironmentSetting(branchName) {
  try {
    const apiBaseUrl = window.QuestionnaireFormData?.apiBaseUrl;
    if (!apiBaseUrl || !branchName) {
      return 'production'; // default
    }

    // Always query production table (branch-metadata-1) to get environment setting
    const url = `${apiBaseUrl}/proxyBranchMetadata?BRANCH_NAME=${encodeURIComponent(cleanBranchName(branchName))}&environment=production`;

    const response = await fetch(url);
    if (!response.ok) {
      console.warn('[fetchEnvironmentSetting] API error:', response.status);
      return 'production';
    }

    const data = await response.json();
    const metadata =
      Array.isArray(data.branchMetadata) && data.branchMetadata.length > 0
        ? data.branchMetadata[0]
        : null;

    const environment = metadata?.environment || 'production';

    // Validate environment value
    const validEnvironments = ['production', 'development', 'staging'];
    if (!validEnvironments.includes(environment)) {
      console.warn(
        '[fetchEnvironmentSetting] Invalid environment value:',
        environment
      );
      return 'production';
    }

    return environment;
  } catch (error) {
    console.error('[fetchEnvironmentSetting] Error:', error);
    return 'production'; // default fallback
  }
}
